import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit as firestoreLimit,
  doc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/utils/firebase.utils";

/**
 * Fetch aggregated historical metrics for a single user within a date range
 * @param {string} userId - User ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {number} limit - Maximum number of records to fetch (optional)
 * @returns {Promise<Object>} - { success: boolean, data: Array, error?: string }
 */
export async function fetchUserHistoricalMetrics(
  userId,
  startDate,
  endDate,
  limit = 100
) {
  try {
    const metricsRef = collection(
      db,
      `users/${userId}/fly_workspaces/default/super_admin_metrices`
    );

    // Convert string dates to Firebase Timestamps for comparison
    const startTimestamp = Timestamp.fromDate(
      new Date(startDate + "T00:00:00")
    );
    const endTimestamp = Timestamp.fromDate(new Date(endDate + "T23:59:59"));

    console.log(
      `Fetching metrics for user ${userId} from ${startDate} to ${endDate}`
    );
    console.log(`Start timestamp:`, startTimestamp);
    console.log(`End timestamp:`, endTimestamp);

    // Try to query by created_at first, but also check for documents by date ID
    let q = query(
      metricsRef,
      where("created_at", ">=", startTimestamp),
      where("created_at", "<=", endTimestamp),
      orderBy("created_at", "desc")
    );

    let querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(
        `No results found with created_at query, trying alternative approach...`
      );

      // Get all documents and filter by date ID
      const allDocsQuery = query(metricsRef, orderBy("created_at", "desc"));
      const allDocsSnapshot = await getDocs(allDocsQuery);

      console.log(
        `Found ${allDocsSnapshot.docs.length} total documents for user ${userId}`
      );

      // Filter documents by date ID (assuming the document ID is the date)
      const filteredDocs = allDocsSnapshot.docs.filter((doc) => {
        const docId = doc.id;
        // Check if document ID is a date in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(docId)) {
          const docDate = new Date(docId);
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          return docDate >= startDateObj && docDate <= endDateObj;
        }
        return false;
      });

      console.log(`Filtered to ${filteredDocs.length} documents in date range`);

      // Convert filtered docs to the expected format
      querySnapshot = {
        docs: filteredDocs,
        empty: filteredDocs.length === 0,
        forEach: function (callback) {
          this.docs.forEach(callback);
        },
      };
    }

    // Apply limit if specified
    if (limit && querySnapshot.docs.length > limit) {
      querySnapshot.docs = querySnapshot.docs.slice(0, limit);
    }
    const data = [];

    // Safety check to ensure querySnapshot is valid
    if (!querySnapshot || typeof querySnapshot.forEach !== "function") {
      console.error("Invalid querySnapshot:", querySnapshot);
      return {
        success: false,
        error: "Invalid query result",
        data: [],
        total: 0,
        dateRange: {
          start: startDate,
          end: endDate,
        },
      };
    }

    querySnapshot.forEach((doc) => {
      const docData = doc.data();
      data.push({
        id: doc.id,
        userId,
        ...docData,
        // Convert Firebase timestamps to readable dates
        fetched_date:
          docData.fetched_date || formatTimestampToDate(docData.created_at),
        created_at: formatTimestamp(docData.created_at),
        updated_at: formatTimestamp(docData.updated_at),
        // Ensure aggregated data exists
        aggregated: docData.aggregated || {},
        // Add computed metrics
        computed: {
          totalImpressions:
            (docData.aggregated?.maps_impressions || 0) +
            (docData.aggregated?.search_impressions || 0),
          totalClicks:
            (docData.aggregated?.website_clicks || 0) +
            (docData.aggregated?.call_clicks || 0),
          ctr: calculateCTR(docData.aggregated),
          engagementRate: calculateEngagementRate(docData.aggregated),
        },
      });
    });

    return {
      success: true,
      data,
      total: data.length,
      dateRange: {
        start: startDate,
        end: endDate,
      },
    };
  } catch (error) {
    console.error("Error fetching user historical metrics:", error);
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
}

/**
 * Fetch aggregated historical metrics for multiple users
 * @param {Array<string>} userIds - Array of user IDs
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {number} limit - Maximum number of records per user (optional)
 * @returns {Promise<Object>} - { success: boolean, data: Array, error?: string }
 */
export async function fetchMultiUserHistoricalMetrics(
  userIds,
  startDate,
  endDate,
  limit = 100
) {
  try {
    const allData = [];
    const errors = [];

    // Fetch data for each user in parallel
    const promises = userIds.map(async (userId) => {
      const result = await fetchUserHistoricalMetrics(
        userId,
        startDate,
        endDate,
        limit
      );
      if (result.success) {
        allData.push(...result.data);
      } else {
        errors.push({ userId, error: result.error });
      }
    });

    await Promise.all(promises);

    // Sort all data by date (most recent first)
    allData.sort((a, b) => {
      const dateA = a.created_at?.toDate
        ? a.created_at.toDate()
        : new Date(a.fetched_date);
      const dateB = b.created_at?.toDate
        ? b.created_at.toDate()
        : new Date(b.fetched_date);
      return dateB - dateA;
    });

    return {
      success: true,
      data: allData,
      total: allData.length,
      errors: errors.length > 0 ? errors : undefined,
      dateRange: {
        start: startDate,
        end: endDate,
      },
      userCount: userIds.length,
    };
  } catch (error) {
    console.error("Error fetching multi-user historical metrics:", error);
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
}

/**
 * Fetch store-wise historical metrics for a user and date
 * @param {string} userId - User ID
 * @param {string} fetchedDate - Specific date (YYYY-MM-DD)
 * @returns {Promise<Object>} - { success: boolean, data: Object, error?: string }
 */
export async function fetchStoreWiseHistoricalMetrics(userId, fetchedDate) {
  try {
    const storeWiseRef = collection(
      db,
      `users/${userId}/fly_workspaces/default/super_admin_metrices/${fetchedDate}/store_wise`
    );

    const querySnapshot = await getDocs(storeWiseRef);
    const storeData = {};

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      storeData[doc.id] = {
        wid: doc.id,
        ...data,
        // Add computed metrics for each store
        computed: {
          totalImpressions:
            (data.maps_impressions || 0) + (data.search_impressions || 0),
          totalClicks: (data.website_clicks || 0) + (data.call_clicks || 0),
          ctr: calculateCTR(data),
          engagementRate: calculateEngagementRate(data),
        },
      };
    });

    return {
      success: true,
      data: storeData,
      storeCount: Object.keys(storeData).length,
      userId,
      fetchedDate,
    };
  } catch (error) {
    console.error("Error fetching store-wise historical metrics:", error);
    return {
      success: false,
      error: error.message,
      data: {},
    };
  }
}

/**
 * Get available dates with metrics data for a user
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of dates to return
 * @returns {Promise<Object>} - { success: boolean, dates: Array, error?: string }
 */
export async function getAvailableMetricsDates(userId, limit = 50) {
  try {
    const metricsRef = collection(
      db,
      `users/${userId}/fly_workspaces/default/super_admin_metrices`
    );

    const q = query(
      metricsRef,
      orderBy("created_at", "desc"),
      firestoreLimit(limit)
    );

    const querySnapshot = await getDocs(q);
    const dates = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const dateStr =
        data.fetched_date || formatTimestampToDate(data.created_at);
      dates.push(dateStr);
    });

    return {
      success: true,
      dates,
      total: dates.length,
    };
  } catch (error) {
    console.error("Error fetching available dates:", error);
    return {
      success: false,
      error: error.message,
      dates: [],
    };
  }
}

/**
 * Get metrics summary for a specific date across multiple users
 * @param {Array<string>} userIds - Array of user IDs
 * @param {string} fetchedDate - Specific date (YYYY-MM-DD)
 * @returns {Promise<Object>} - { success: boolean, data: Object, error?: string }
 */
export async function getMetricsSummaryForDate(userIds, fetchedDate) {
  try {
    const summaryData = {
      aggregated: {
        maps_impressions: 0,
        search_impressions: 0,
        directions: 0,
        website_clicks: 0,
        call_clicks: 0,
        bookings: 0,
        posts: 0,
        reviews: 0,
        rating: 0,
        rank: 0,
        total_stores: 0,
      },
      userCount: userIds.length,
      usersWithData: 0,
      fetchedDate,
    };

    const promises = userIds.map(async (userId) => {
      try {
        const docRef = doc(
          db,
          `users/${userId}/fly_workspaces/default/super_admin_metrices/${fetchedDate}`
        );
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.aggregated) {
            summaryData.usersWithData++;

            // Sum up all numeric metrics
            Object.keys(summaryData.aggregated).forEach((key) => {
              if (key === "rating" || key === "rank") {
                // For rating and rank, we'll calculate average later
                return;
              }
              summaryData.aggregated[key] += data.aggregated[key] || 0;
            });

            return data.aggregated;
          }
        }
        return null;
      } catch (error) {
        console.warn(`Error fetching data for user ${userId}:`, error);
        return null;
      }
    });

    const results = await Promise.all(promises);
    const validResults = results.filter((result) => result !== null);

    // Calculate averages for rating and rank
    if (validResults.length > 0) {
      const totalRating = validResults.reduce(
        (sum, result) => sum + (result.rating || 0),
        0
      );
      const totalRank = validResults.reduce(
        (sum, result) => sum + (result.rank || 0),
        0
      );

      summaryData.aggregated.rating = totalRating / validResults.length;
      summaryData.aggregated.rank = totalRank / validResults.length;
    }

    // Add computed metrics
    summaryData.computed = {
      totalImpressions:
        summaryData.aggregated.maps_impressions +
        summaryData.aggregated.search_impressions,
      totalClicks:
        summaryData.aggregated.website_clicks +
        summaryData.aggregated.call_clicks,
      ctr: calculateCTR(summaryData.aggregated),
      engagementRate: calculateEngagementRate(summaryData.aggregated),
    };

    return {
      success: true,
      data: summaryData,
    };
  } catch (error) {
    console.error("Error getting metrics summary:", error);
    return {
      success: false,
      error: error.message,
      data: null,
    };
  }
}

/**
 * Get trend data for a specific metric across time periods
 * @param {Array<string>} userIds - Array of user IDs
 * @param {string} metric - Metric name (e.g., 'maps_impressions', 'rank')
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} - { success: boolean, data: Array, error?: string }
 */
export async function getMetricTrendData(userIds, metric, startDate, endDate) {
  try {
    const result = await fetchMultiUserHistoricalMetrics(
      userIds,
      startDate,
      endDate
    );

    if (!result.success) {
      return result;
    }

    // Group data by date and aggregate the metric
    const trendData = {};

    result.data.forEach((record) => {
      const date =
        record.fetched_date || formatTimestampToDate(record.created_at);
      if (!trendData[date]) {
        trendData[date] = {
          date,
          values: [],
          total: 0,
          average: 0,
          count: 0,
        };
      }

      const value = record.aggregated?.[metric] || 0;
      trendData[date].values.push(value);
      trendData[date].total += value;
      trendData[date].count++;
    });

    // Calculate averages and sort by date
    const trendArray = Object.values(trendData)
      .map((item) => ({
        ...item,
        average: item.count > 0 ? item.total / item.count : 0,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      success: true,
      data: trendArray,
      metric,
      dateRange: { start: startDate, end: endDate },
      totalDataPoints: trendArray.length,
    };
  } catch (error) {
    console.error("Error getting trend data:", error);
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
}

// Helper functions
function calculateCTR(metrics) {
  const totalImpressions =
    (metrics?.maps_impressions || 0) + (metrics?.search_impressions || 0);
  const totalClicks =
    (metrics?.website_clicks || 0) + (metrics?.call_clicks || 0);

  if (totalImpressions === 0) return 0;
  return (totalClicks / totalImpressions) * 100;
}

function calculateEngagementRate(metrics) {
  const totalImpressions =
    (metrics?.maps_impressions || 0) + (metrics?.search_impressions || 0);
  const totalEngagements =
    (metrics?.website_clicks || 0) +
    (metrics?.call_clicks || 0) +
    (metrics?.directions || 0) +
    (metrics?.bookings || 0);

  if (totalImpressions === 0) return 0;
  return (totalEngagements / totalImpressions) * 100;
}

/**
 * Generate date range array
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Array<string>} - Array of dates in YYYY-MM-DD format
 */
export function generateDateRange(startDate, endDate) {
  const dates = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  for (
    let date = new Date(start);
    date <= end;
    date.setDate(date.getDate() + 1)
  ) {
    dates.push(date.toISOString().split("T")[0]);
  }

  return dates;
}

/**
 * Get date range based on preset (7d, 30d, 90d)
 * @param {string} preset - Preset range ('7d', '30d', '90d')
 * @returns {Object} - { startDate, endDate }
 */
export function getPresetDateRange(preset) {
  const endDate = new Date();
  const startDate = new Date();

  const days =
    {
      "7d": 7,
      "30d": 30,
      "90d": 90,
    }[preset] || 30;

  startDate.setDate(startDate.getDate() - days);

  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
}

/**
 * Export metrics data to CSV format
 * @param {Array} data - Historical metrics data
 * @param {Array} columns - Column definitions
 * @returns {string} - CSV content
 */
export function exportMetricsToCSV(data, columns) {
  if (!data || data.length === 0) return "";

  // Create header row
  const headers = columns
    .map((col) => col.text || col.label || col.key)
    .join(",");

  // Create data rows
  const rows = data
    .map((item) => {
      return columns
        .map((col) => {
          const value = col.value ? item[col.value] : item[col.key];
          // Handle nested values (e.g., aggregated.maps_impressions)
          const finalValue =
            col.value && col.value.includes(".")
              ? col.value.split(".").reduce((obj, key) => obj?.[key], item)
              : value;

          // Escape commas and quotes in CSV
          if (
            typeof finalValue === "string" &&
            (finalValue.includes(",") || finalValue.includes('"'))
          ) {
            return `"${finalValue.replace(/"/g, '""')}"`;
          }

          return finalValue || "";
        })
        .join(",");
    })
    .join("\n");

  return `${headers}\n${rows}`;
}

// Helper functions for Firebase timestamp handling
export function formatTimestamp(timestamp) {
  if (!timestamp) return null;
  let date;
  if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else {
    date = new Date(timestamp);
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatTimestampToDate(timestamp) {
  if (!timestamp) {
    const today = new Date();
    return today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  let date;
  if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else {
    date = new Date(timestamp);
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Additional utility function for consistent date formatting
export function formatDateToDDMMMYYYY(dateInput) {
  if (!dateInput) return "N/A";

  let date;
  if (dateInput.toDate) {
    date = dateInput.toDate();
  } else if (dateInput.seconds) {
    date = new Date(dateInput.seconds * 1000);
  } else {
    date = new Date(dateInput);
  }

  if (isNaN(date.getTime())) return "Invalid Date";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
